// @ts-nocheck
/**
 * API client configuration
 */

import axios, { AxiosError } from 'axios';
import type { LoginCredentials, RegisterData, AuthResponse, User, ApiError } from '@/types/auth';
import {
  getToken,
  removeToken,
  getParentPortalToken,
  removeParentPortalToken
} from './storageUtils';

// Use environment variable directly
const getBaseURL = (): string => {
  const envUrl = process.env.NEXT_PUBLIC_API_URL;
  if (envUrl && envUrl !== 'undefined' && envUrl.trim() !== '') {
    return envUrl;
  }
  // Fallback to vibe API
  return 'https://vibe-api.intrazero.app/api';
};

// Create axios instance
export const api = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000,
  withCredentials: true,
});

// Add request interceptor for error handling and token injection
api.interceptors.request.use(
  (config) => {
    // Inject token if available (fall back for cross-domain cookie issues)
    const token = getToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor to handle errors and timeouts
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      console.error('API Request Timeout:', error.config?.url);
      error.message = 'Request timeout - please check your connection';
    } else if (error.code === 'ERR_NETWORK' || !error.response) {
      console.error('API Network Error:', error.config?.url);
      error.message = 'Network error - please check your connection';
    }
    return Promise.reject(error);
  }
);

// Admin API
export interface SystemStatistics {
  users: {
    total: number;
    active: number;
    active_today: number;
    by_role: Record<string, number>;
  };
  materials: {
    total: number;
    by_type: Record<string, number>;
    storage_mb: number;
  };
  subjects: {
    total: number;
    public: number;
    private: number;
  };
  usage: {
    daily: {
      sessions: number;
      questions: number;
      conversations: number;
    };
    weekly: {
      sessions: number;
      questions: number;
    };
    monthly: {
      sessions: number;
      questions: number;
    };
  };
  timestamp: string;
}

export interface HealthStatus {
  status: 'healthy' | 'degraded' | 'down' | 'not_configured';
  error?: string;
  response_time_ms?: number;
  timestamp: string;
}

export interface SystemHealth {
  overall: 'healthy' | 'degraded' | 'down';
  services: {
    database?: HealthStatus;
    qdrant?: HealthStatus;
    redis?: HealthStatus;
    storage?: HealthStatus;
  };
  timestamp: string;
}

export interface UsageAnalytics {
  period_days: number;
  sessions: number;
  questions: number;
  conversations: number;
  active_users: number;
  timestamp: string;
}

export interface UserAnalytics {
  total: number;
  active: number;
  new_last_30_days: number;
  by_role: Record<string, number>;
  timestamp: string;
}

export interface ProviderAnalytics {
  providers: Array<{
    name: string;
    is_active: boolean;
    is_default: boolean;
    total_requests: number;
    successful_requests: number;
    failed_requests: number;
    avg_response_time_ms: number;
  }>;
  timestamp: string;
}

export interface StorageAnalytics {
  total_materials: number;
  by_type: Record<string, number>;
  total_storage_mb: number;
  timestamp: string;
}

export interface CacheStats {
  status: 'available' | 'not_configured' | 'error';
  hit_rate?: number;
  miss_rate?: number;
  total_keys?: number;
  error?: string;
  timestamp: string;
}

export const adminApi = {
  async getStatistics(): Promise<SystemStatistics> {
    const { data } = await api.get<SystemStatistics>('/v1/admin/statistics');
    return data;
  },

  async getSystemHealth(): Promise<SystemHealth> {
    const { data } = await api.get<SystemHealth>('/v1/admin/health');
    return data;
  },

  async getDatabaseHealth(): Promise<HealthStatus> {
    const { data } = await api.get<HealthStatus>('/v1/admin/health/database');
    return data;
  },

  async getQdrantHealth(): Promise<HealthStatus> {
    const { data } = await api.get<HealthStatus>('/v1/admin/health/qdrant');
    return data;
  },

  async getRedisHealth(): Promise<HealthStatus> {
    const { data } = await api.get<HealthStatus>('/v1/admin/health/redis');
    return data;
  },

  async getS3Health(): Promise<HealthStatus> {
    const { data } = await api.get<HealthStatus>('/v1/admin/health/s3');
    return data;
  },

  async getQueueStatus(): Promise<any> {
    const { data } = await api.get('/v1/admin/queues');
    return data;
  },

  async getUsageAnalytics(days: number = 30): Promise<UsageAnalytics> {
    const { data } = await api.get<UsageAnalytics>('/v1/admin/analytics/usage', {
      params: { days }
    });
    return data;
  },

  async getUserAnalytics(): Promise<UserAnalytics> {
    const { data } = await api.get<UserAnalytics>('/v1/admin/analytics/users');
    return data;
  },

  async getProviderAnalytics(): Promise<ProviderAnalytics> {
    const { data } = await api.get<ProviderAnalytics>('/v1/admin/analytics/providers');
    return data;
  },

  async getStorageAnalytics(): Promise<StorageAnalytics> {
    const { data } = await api.get<StorageAnalytics>('/v1/admin/analytics/storage');
    return data;
  },

  async getCacheStats(): Promise<CacheStats> {
    const { data } = await api.get<CacheStats>('/v1/admin/cache/stats');
    return data;
  },

  async clearCache(pattern?: string): Promise<{ status: string; cleared: unknown; timestamp: string }> {
    const { data } = await api.post('/v1/admin/cache/clear', null, {
      params: pattern ? { pattern } : {}
    });
    return data;
  },

  async getAuditLogs(params: {
    page?: number;
    page_size?: number;
    user_id?: string;
    action?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<any> {
    const { data } = await api.get('/v1/admin/audit/logs', { params });
    return data;
  },

  async getApplicationLogs(level?: string, limit: number = 100): Promise<any> {
    const { data } = await api.get('/v1/admin/logs', {
      params: { level, limit }
    });
    return data;
  },

  async searchLogs(query: string, level?: string, limit: number = 100): Promise<any> {
    const { data } = await api.get('/v1/admin/logs/search', {
      params: { query, level, limit }
    });
    return data;
  }
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Check if this is a parent portal request
    const isParentPortalRequest = config.url?.includes('/v1/sis/parent-portal') ||
      config.url?.includes('/v1/sis/parent-communications');

    if (isParentPortalRequest) {
      // Use parent portal token for parent portal requests
      const parentToken = localStorage.getItem('parent_portal_token');
      if (parentToken) {
        config.headers.Authorization = `Bearer ${parentToken}`;
      }
    } else {
      // Use main token for all other requests
      const token = getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    if (error.response?.status === 401) {
      const isParentPortalRequest = error.config?.url?.includes('/v1/sis/parent-portal') ||
        error.config?.url?.includes('/v1/sis/parent-communications');

      if (isParentPortalRequest) {
        // Parent portal 401 - redirect to parent portal login
        const parentToken = getParentPortalToken();
        if (parentToken && !window.location.pathname.includes('/parent-portal/login')) {
          removeParentPortalToken();
          window.location.href = '/parent-portal/login';
        }
      } else {
        // Main app 401 - redirect to main login
        // Always check if we should redirect, not just when token exists
        // (token might have been cleared already)
        if (!window.location.pathname.includes('/auth/login')) {
          // Clear all auth data
          removeToken();
          // Clear localStorage items
          if (typeof window !== 'undefined') {
            localStorage.removeItem('user_type');
            localStorage.removeItem('impersonating');
            localStorage.removeItem('original_admin_token');
          }
          // Redirect to login
          window.location.href = '/auth/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export interface ImpersonateResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  impersonated_by?: {
    id: string;
    email: string;
    full_name: string;
  };
  target_user?: {
    id: string;
    email: string;
    full_name: string;
  };
}

export const authApi = {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const { data } = await api.post<AuthResponse>('/v1/auth/login', credentials);
    return data;
  },

  async register(userData: RegisterData): Promise<User> {
    const { data } = await api.post<User>('/v1/auth/register', userData);
    return data;
  },

  async getCurrentUser(token?: string): Promise<User> {
    // Use provided token or get from storage
    const authToken = token || getToken();
    const { data } = await api.get<User>('/v1/auth/me', {
      headers: authToken ? {
        Authorization: `Bearer ${authToken}`
      } : {}
    });
    return data;
  },

  async updateProfile(profileData: { full_name?: string; email?: string }): Promise<User> {
    const { data } = await api.put<User>('/v1/auth/me', profileData);
    return data;
  },

  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    await api.post('/v1/auth/change-password', {
      old_password: oldPassword,
      new_password: newPassword,
    });
  },

  async verifyEmail(otp: string, email?: string): Promise<{ message: string; verified: boolean }> {
    const { data } = await api.post<{ message: string; verified: boolean }>('/v1/auth/verify-email', {
      otp,
      email,
    });
    return data;
  },

  async resendVerificationEmail(email: string): Promise<{ message: string }> {
    // Backend uses POST with email as query parameter
    const { data } = await api.post<{ message: string }>('/v1/auth/resend-verification', {}, {
      params: { email },
    });
    return data;
  },

  async forgotPassword(email: string, turnstileToken?: string): Promise<{ message: string }> {
    const { data } = await api.post<{ message: string }>('/v1/auth/forgot-password', {}, {
      params: { email, turnstile_token: turnstileToken },
    });
    return data;
  },

  async resetPassword(otp: string, newPassword: string, email: string): Promise<{ message: string }> {
    const { data } = await api.post<{ message: string }>('/v1/auth/reset-password', {
      otp,
      new_password: newPassword,
      email,
    });
    return data;
  },

  async impersonate(userId: string): Promise<ImpersonateResponse> {
    const { data } = await api.post<ImpersonateResponse>(`/v1/auth/impersonate/${userId}`);
    return data;
  },

  async logout() {
    try {
      await api.post('/v1/auth/logout');
    } catch (err) {
      console.error('Logout API error:', err);
    }
    removeToken();
    if (typeof window !== 'undefined') {
      window.location.href = '/auth/login';
    }
  },
};

// Subjects API
export interface Subject {
  id: string;
  title: string;
  subject_code: string;
  language: string;
  visibility: string;
  owner_id: string;
  owner_name?: string;
  material_count?: number;
  class_id?: string;
  class_name?: string;
  reasoning_template_id?: string;
  custom_reasoning_instructions?: string;
  subject_type?: 'educational' | 'customer_agent';  // Added for Customer Agent Mode
  customer_agent_settings?: Record<string, any>;  // Added for Customer Agent Mode
  is_pinned?: boolean;  // Pin status for current user
  created_at: string;
  updated_at: string;
}

export interface SubjectCreate {
  title: string;
  subject_code: string;
  language?: string;
  visibility?: string;
  class_id?: string;
  reasoning_template_id?: string;
  custom_reasoning_instructions?: string;
}

export interface SubjectsListResponse {
  items: Subject[];
  total: number;
  page: number;
  page_size: number;
}

export const subjectsApi = {
  async list(page = 1, pageSize = 20, subject_code?: string, language?: string, class_id?: string, search?: string): Promise<SubjectsListResponse> {
    const params: unknown = { page, page_size: pageSize };
    if (subject_code) params.subject_code = subject_code;
    if (language) params.language = language;
    if (class_id) params.class_id = class_id;
    if (search) params.search = search;

    const { data } = await api.get<SubjectsListResponse>('/v1/subjects', {
      params,
    });
    return data;
  },

  async get(id: string): Promise<Subject> {
    const { data } = await api.get<Subject>(`/v1/subjects/${id}`);
    return data;
  },

  async create(subject: SubjectCreate): Promise<Subject> {
    const { data } = await api.post<Subject>('/v1/subjects', subject);
    return data;
  },

  async update(id: string, updates: Partial<SubjectCreate & { customer_agent_settings?: Record<string, any>; subject_type?: string }>): Promise<Subject> {
    const { data } = await api.patch<Subject>(`/v1/subjects/${id}`, updates);
    return data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/v1/subjects/${id}`);
  },

  async pin(id: string): Promise<void> {
    await api.post(`/v1/subjects/${id}/pin`);
  },

  async unpin(id: string): Promise<void> {
    await api.delete(`/v1/subjects/${id}/pin`);
  },
};

// Analytics API - defined below after interfaces

export const conversationsApi = {
  async list(page = 1, pageSize = 20, subject_thread_id?: string): Promise<any> {
    const params: unknown = { page, page_size: pageSize };
    if (subject_thread_id) params.subject_thread_id = subject_thread_id;
    const { data } = await api.get('/v1/conversations', { params });
    return data;
  },
  async getRecent(limit = 5): Promise<any[]> {
    const { data } = await api.get('/v1/conversations', {
      params: { page: 1, page_size: limit },
    });
    return data.items || [];
  },
};

// Reasoning Templates API
export interface ReasoningTemplate {
  id: string;
  name: string;
  description?: string;
  instructions_text: string;
  is_active: boolean;
  created_by: string;
  creator_name?: string;
  created_at: string;
  updated_at: string;
}

export interface ReasoningTemplateCreate {
  name: string;
  description?: string;
  instructions_text: string;
  is_active?: boolean;
}

export interface ReasoningTemplateUpdate {
  name?: string;
  description?: string;
  instructions_text?: string;
  is_active?: boolean;
}

export interface ReasoningTemplatesListResponse {
  items: ReasoningTemplate[];
  total: number;
  page: number;
  page_size: number;
}

// Routing Policies API
export interface RoutingPolicy {
  id: string;
  name: string;
  description?: string;
  conditions: {
    user_role?: string[];
    time_range?: { start: string; end: string };
    model_required?: string;
    subject_id?: string[];
    user_id?: string[];
    max_tokens?: number;
    temperature_range?: { min: number; max: number };
  };
  provider_priority: string;
  fallback_providers?: string;
  is_active: boolean;
  priority: number;
  is_default: boolean;
  created_by: string;
  creator_name?: string;
  created_at: string;
  updated_at: string;
}

export interface RoutingPolicyCreate {
  name: string;
  description?: string;
  conditions: RoutingPolicy['conditions'];
  provider_priority: string;
  fallback_providers?: string;
  is_active?: boolean;
  priority?: number;
  is_default?: boolean;
}

export interface RoutingPolicyUpdate {
  name?: string;
  description?: string;
  conditions?: RoutingPolicy['conditions'];
  provider_priority?: string;
  fallback_providers?: string;
  is_active?: boolean;
  priority?: number;
  is_default?: boolean;
}

export interface RoutingPolicyListResponse {
  items: RoutingPolicy[];
  total: number;
  page: number;
  page_size: number;
}

export interface PolicyMatchResult {
  policy_id?: string;
  policy_name?: string;
  matched: boolean;
  matched_conditions: string[];
  provider_priority?: string[];
}

export const routingPoliciesApi = {
  async list(params?: { page?: number; page_size?: number; is_active?: boolean }): Promise<RoutingPolicyListResponse> {
    const { data } = await api.get<RoutingPolicyListResponse>('/v1/routing-policies', { params });
    return data;
  },

  async get(id: string): Promise<RoutingPolicy> {
    const { data } = await api.get<RoutingPolicy>(`/v1/routing-policies/${id}`);
    return data;
  },

  async create(policy: RoutingPolicyCreate): Promise<RoutingPolicy> {
    const { data } = await api.post<RoutingPolicy>('/v1/routing-policies', policy);
    return data;
  },

  async update(id: string, policy: RoutingPolicyUpdate): Promise<RoutingPolicy> {
    const { data } = await api.patch<RoutingPolicy>(`/v1/routing-policies/${id}`, policy);
    return data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/v1/routing-policies/${id}`);
  },

  async test(context: Record<string, any>): Promise<PolicyMatchResult> {
    const { data } = await api.post<PolicyMatchResult>('/v1/routing-policies/test', context);
    return data;
  },
};

export const reasoningTemplatesApi = {
  async list(page = 1, pageSize = 20, is_active?: boolean): Promise<ReasoningTemplatesListResponse> {
    const params: unknown = { page, page_size: pageSize };
    if (is_active !== undefined) params.is_active = is_active;

    const { data } = await api.get<ReasoningTemplatesListResponse>('/v1/reasoning-templates', {
      params,
    });
    return data;
  },

  async get(id: string): Promise<ReasoningTemplate> {
    const { data } = await api.get<ReasoningTemplate>(`/v1/reasoning-templates/${id}`);
    return data;
  },

  async create(template: ReasoningTemplateCreate): Promise<ReasoningTemplate> {
    const { data } = await api.post<ReasoningTemplate>('/v1/reasoning-templates', template);
    return data;
  },

  async update(id: string, updates: ReasoningTemplateUpdate): Promise<ReasoningTemplate> {
    const { data } = await api.patch<ReasoningTemplate>(`/v1/reasoning-templates/${id}`, updates);
    return data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/v1/reasoning-templates/${id}`);
  },
};

// ==================== Mastery API ====================
export interface MasteryOverview {
  user_id: string;
  subject_thread_id?: string;
  total_topics: number;
  average_mastery: number;
  mastery_distribution: {
    high: number;
    medium: number;
    low: number;
  };
  topics: Record<string, { mastery_level: number; level: string }>;
}

export interface MasteryTrend {
  date: string;
  mastery_level: number;
}

export interface MasteryHeatmapData {
  date: string;
  topic: string;
  mastery_level: number;
}

export const masteryApi = {
  async getOverview(userId: string, subjectThreadId?: string): Promise<MasteryOverview> {
    const { data } = await api.get<MasteryOverview>(`/v1/mastery/${userId}`, {
      params: subjectThreadId ? { subject_thread_id: subjectThreadId } : {},
    });
    return data;
  },

  async getSubjectMastery(userId: string, subjectId: string) {
    const { data } = await api.get(`/v1/mastery/${userId}/subject/${subjectId}`);
    return data;
  },

  async getTopics(userId: string, subjectThreadId?: string, topic?: string) {
    const params: unknown = {};
    if (subjectThreadId) params.subject_thread_id = subjectThreadId;
    if (topic) params.topic = topic;
    const { data } = await api.get(`/v1/mastery/${userId}/topics`, { params });
    return data;
  },

  async getHeatmap(userId: string, subjectThreadId?: string, startDate?: string, endDate?: string) {
    const params: unknown = {};
    if (subjectThreadId) params.subject_thread_id = subjectThreadId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const { data } = await api.get<{ data: MasteryHeatmapData[] }>(`/v1/mastery/${userId}/heatmap`, { params });
    return data;
  },

  async getTrends(userId: string, topic?: string, subjectThreadId?: string, days = 30): Promise<{ trends: MasteryTrend[], topics?: unknown[] }> {
    const params: unknown = { days };
    if (topic) params.topic = topic;
    if (subjectThreadId) params.subject_thread_id = subjectThreadId;
    const { data } = await api.get<{ trends: MasteryTrend[], topics?: unknown[] }>(`/v1/mastery/${userId}/trends`, { params });
    return data;
  },
};

// ==================== Analytics API ====================
export interface Recommendation {
  type: string;
  priority: number;
  title: string;
  description: string;
  topic?: string;
  material_id?: string;
  actions: string[];
  estimated_time: string;
}

export interface RiskAssessment {
  user_id: string;
  risk_score: number;
  risk_category: string;
  risk_factors: Record<string, number>;
  recommendations: string[];
}

export const analyticsApi = {
  // Legacy methods
  async getDashboardStats(): Promise<{ subjects: number; materials: number; conversations: number }> {
    const { data } = await api.get<{ subjects: number; materials: number; conversations: number }>('/v1/analytics/dashboard/stats');
    return data;
  },
  async getAdminDashboard(): Promise<any> {
    const { data } = await api.get('/v1/analytics/admin/dashboard');
    return data;
  },

  // Phase 8 Analytics methods
  async getOverview(userId?: string, subjectThreadId?: string) {
    const params: unknown = {};
    if (userId) params.user_id = userId;
    if (subjectThreadId) params.subject_thread_id = subjectThreadId;
    const { data } = await api.get('/v1/analytics/overview', { params });
    return data;
  },

  async getPerformance(userId?: string, subjectThreadId?: string) {
    const params: unknown = {};
    if (userId) params.user_id = userId;
    if (subjectThreadId) params.subject_thread_id = subjectThreadId;
    const { data } = await api.get('/v1/analytics/performance', { params });
    return data;
  },

  async getEngagement(userId?: string, subjectThreadId?: string, days = 30) {
    const params: unknown = { days };
    if (userId) params.user_id = userId;
    if (subjectThreadId) params.subject_thread_id = subjectThreadId;
    const { data } = await api.get('/v1/analytics/engagement', { params });
    return data;
  },

  async getProgress(userId?: string, subjectThreadId?: string, days = 30) {
    const params: unknown = { days };
    if (userId) params.user_id = userId;
    if (subjectThreadId) params.subject_thread_id = subjectThreadId;
    const { data } = await api.get('/v1/analytics/progress', { params });
    return data;
  },

  async getTrends(userId?: string, subjectThreadId?: string, days = 30) {
    const params: unknown = { days };
    if (userId) params.user_id = userId;
    if (subjectThreadId) params.subject_thread_id = subjectThreadId;
    const { data } = await api.get('/v1/analytics/trends', { params });
    return data;
  },

  async getRisk(userId: string, subjectThreadId?: string): Promise<RiskAssessment> {
    const params: unknown = {};
    if (subjectThreadId) params.subject_thread_id = subjectThreadId;
    const { data } = await api.get<RiskAssessment>(`/v1/analytics/risk/${userId}`, { params });
    return data;
  },

  async getAtRiskStudents(subjectThreadId?: string, minRiskScore = 50) {
    const params: unknown = { min_risk_score: minRiskScore };
    if (subjectThreadId) params.subject_thread_id = subjectThreadId;
    const { data } = await api.get('/v1/analytics/at-risk-students', { params });
    return data;
  },

  async getRecommendations(userId: string, subjectThreadId?: string, limit = 10): Promise<{ recommendations: Recommendation[] }> {
    const params: unknown = { limit };
    if (subjectThreadId) params.subject_thread_id = subjectThreadId;
    const { data } = await api.get<{ recommendations: Recommendation[] }>(`/v1/analytics/recommendations/${userId}`, { params });
    return data;
  },

  async generateDiagnosticQuiz(userId: string, subjectThreadId: string, numQuestions = 10) {
    const { data } = await api.post('/v1/analytics/diagnostic/quiz', null, {
      params: { user_id: userId, subject_thread_id: subjectThreadId, num_questions: numQuestions },
    });
    return data;
  },

  async getDiagnosticResults(quizId: string, userId: string) {
    const { data } = await api.get(`/v1/analytics/diagnostic/${quizId}/results`, {
      params: { user_id: userId },
    });
    return data;
  },
};

// Reports API (Phase 8)
export const reportsApi = {
  async generateReport(reportType: string, format: string, parameters: unknown) {
    const { data } = await api.post('/v1/reports/generate', {
      report_type: reportType,
      format: format,
      parameters: parameters,
    });
    return data;
  },

  async getReportStatus(reportId: string) {
    const { data } = await api.get(`/v1/reports/${reportId}/status`);
    return data;
  },

  async downloadReport(reportId: string) {
    const response = await api.get(`/v1/reports/${reportId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  async getReportHistory(page: number = 1, pageSize: number = 20) {
    const { data } = await api.get('/v1/reports/history', {
      params: { page, page_size: pageSize },
    });
    return data;
  },
};

export default api;
